import React, { useState } from "react";
import bcrypt from "bcryptjs";
import { useNavigate } from "react-router-dom";
import { loginMemberStackApi } from "../Api";
import { useAuth } from "../Router";
import { MemberstackProvider, useMemberstack } from "@memberstack/react";

const memberstackConfig = {
  publicKey: "pk_b3d30192cc0bae7dfb0d",
  secretKey: "sk_e23b5baa724e3fa72fa4",
};

const OnboardingStep8 = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  return (
    <MemberstackProvider config={memberstackConfig}>
      <MemberstackContent
        isLoading={isLoading}
        emailError={emailError}
        passwordError={passwordError}
        termsError={termsError}
        showPassword={showPassword}
        togglePasswordVisibility={togglePasswordVisibility}
        errorMessage={errorMessage}
        setEmailError={setEmailError}
        setPasswordError={setPasswordError}
        setTermsError={setTermsError}
        setErrorMessage={setErrorMessage}
      />
    </MemberstackProvider>
  );
};

const MemberstackContent = ({
  isLoading,
  emailError,
  passwordError,
  termsError,
  showPassword,
  togglePasswordVisibility,
  errorMessage,
  setEmailError,
  setPasswordError,
  setTermsError,
  setErrorMessage,
}) => {
  const memberstack = useMemberstack();
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleMemberstackSignUp = async () => {
    const emailInput = document.getElementById("email");
    const passwordInput = document.getElementById("password");
    const email = emailInput.value;
    const password = passwordInput.value;

    setEmailError(false);
    setPasswordError(false);
    setTermsError(false);
    setErrorMessage("");

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;

    if (!email || !emailPattern.test(email)) {
      setEmailError(true);
      setErrorMessage("Hmm… that doesn’t look like a valid email. Try again!");
    }
    if (!password || !passwordPattern.test(password)) {
      setPasswordError(true);
      setErrorMessage(
        "Your password needs to be a bit stronger. Try adding a mix of letters, numbers, or symbols."
      );
    }
    if (!email && !password) {
      setErrorMessage(
        "Oops! Looks like something’s missing. Let’s fill in the blanks."
      );
    }

    if (emailError || passwordError) {
      return;
    }

    const hashedPassword = bcrypt.hashSync(password, 10);

    // Parse recovery_cohorts from localStorage if present and valid
    let recoveryCohortsValue = undefined;
    const recoveryCohortsRaw = localStorage.getItem("recovery_cohorts");
    if (recoveryCohortsRaw) {
      try {
        // Try to parse as JSON, but if it's already a string, just use it
        const parsed = JSON.parse(recoveryCohortsRaw);
        // If parsed is an object or array, store as JSON string
        if (typeof parsed === "object") {
          recoveryCohortsValue = JSON.stringify(parsed);
        } else {
          // If parsed is a primitive, store as string
          recoveryCohortsValue = String(parsed);
        }
      } catch (e) {
        // If parsing fails, just use the raw string
        recoveryCohortsValue = recoveryCohortsRaw;
      }
    }

    const data = {
      age: localStorage.getItem("age"),
      gender: localStorage.getItem("gender"),
      patient_name: localStorage.getItem("patient_name"),
      ...(localStorage.getItem("surgery_date") && {
        surgery_date: localStorage.getItem("surgery_date"),
      }),
      onset_of_pain: localStorage.getItem("onset_of_pain") || "",
      is_preventive: JSON.parse(localStorage.getItem("is_preventive")),
      pain: localStorage.getItem("pain_level"),
      motivation: localStorage.getItem("motivation"),
      activity_level: localStorage.getItem("activity_level"),
      which_knee: localStorage.getItem("which_knee"),
      personal_goal: localStorage.getItem("personal_goal"),
      day_zero: localStorage.getItem("day_zero"),
      recovery_cohorts: recoveryCohortsValue,
      password: "",
    };

    try {
      const memberstackResponse = await memberstack.signupMemberEmailPassword({
        email,
        password,
      });

      if (!memberstackResponse.data) {
        throw new Error("Memberstack registration failed");
      }

      console.log("Memberstack registration success:", memberstackResponse);

      data.username = memberstackResponse.data.member.auth.email;
      data.memberstack_id = memberstackResponse.data.member.id;

      const response = await fetch("https://api-k8s.engiv.io/save", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      console.log(data);

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const memberStackResponse = await loginMemberStackApi(
        data.memberstack_id
      );
      if (memberStackResponse) {
        login();
        setTimeout(() => {
          window.location.href = "/?onboarding=true";
        }, 10);
      } else {
        throw new Error("Memberstack login failed");
      }
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  return (
    <div className="modal-container animated-onboarding onboarding-step8 onboarding-login fadeIn">
      {isLoading ? (
        <div className="video-container signup-loader">
          <h3>
            Please wait while we're
            <br />
            setting up things for you...
          </h3>
          <video src="../videos/rippleEffect.mp4" autoPlay loop></video>
        </div>
      ) : (
        <>
          <div className="modal-container-title heading fade1">
            <h2>Create an Account</h2>
            <h3>Join us to get started with your journey</h3>
          </div>

          <div className="modal-contect-title label fade2">
            <div className="login-fields">
              <div>
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  className={`input-email ${emailError ? "error" : ""}`}
                  placeholder="You@example.com"
                  id="email"
                />
              </div>
              <div>
                <label htmlFor="password">Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`input-password ${passwordError ? "error" : ""}`}
                    placeholder="********"
                    id="password"
                  />
                  <span
                    onClick={togglePasswordVisibility}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className={
                        showPassword ? "ico-hide-password" : "ico-show-password"
                      }
                    ></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="signup fade3">
            <p>
              {" "}
              {errorMessage && <p className="error-message">{errorMessage}</p>}
            </p>
            <button
              className="btn-next next-btn memberstack-signup"
              onClick={handleMemberstackSignUp}
            >
              Let's Go
            </button>
          </div>
          <div className="terms-container fade3">
            <label htmlFor="terms">
              By continuing, you agree to our{" "}
              <a href="https://www.engivio.com/term" target="_new">
                Terms
              </a>{" "}
              and{" "}
              <a href="https://www.engivio.com/privacy" target="_new">
                Privacy Policy.
              </a>
            </label>
          </div>
          <div className="separator fade3"></div>
          <p className="fade3">
            Already signed up? <a href="/patient-login">Log in</a>
          </p>
        </>
      )}
    </div>
  );
};

export default OnboardingStep8;
